// Hook useToast para manejar notificaciones Toast.
// Proporciona funciones para mostrar diferentes tipos de alertas.
// Reemplaza los alert() del navegador con una experiencia más moderna.
// Usa un contexto global para compartir el estado de toasts entre componentes.

import { useState, useCallback, createContext, useContext, useEffect } from 'react';
import ToastContainer from '../components/UI/Toast';

// Generador de IDs únicos para los toasts
let toastIdCounter = 0;
const generateToastId = () => `toast-${toastIdCounter++}`;

// Contexto global para el estado de toasts
const ToastContext = createContext(null);

/**
 * Provider global para el sistema de toasts.
 * Renderiza el ToastContainer y proporciona las funciones de toast a todos los componentes.
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [position, setPosition] = useState('bottom-center');

  // Función para agregar un nuevo toast
  const addToast = useCallback((toast) => {
    const newToast = {
      id: generateToastId(),
      type: 'info',
      duration: 4000,
      ...toast
    };

    setToasts((prev) => [...prev, newToast]);
    return newToast.id;
  }, []);

  // Función para remover un toast
  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Funciones de conveniencia para cada tipo
  const showSuccess = useCallback((message, options = {}) => {
    return addToast({ type: 'success', message, ...options });
  }, [addToast]);

  const showError = useCallback((message, options = {}) => {
    return addToast({ type: 'error', message, ...options });
  }, [addToast]);

  const showWarning = useCallback((message, options = {}) => {
    return addToast({ type: 'warning', message, ...options });
  }, [addToast]);

  const showInfo = useCallback((message, options = {}) => {
    return addToast({ type: 'info', message, ...options });
  }, [addToast]);

  // Muestra un toast de estado loading que permanece hasta ser removido manualmente.
  // Útil para operaciones asíncronas donde se necesita controlar cuándo ocultar el indicador.
  const showLoading = useCallback((message, options = {}) => {
    return addToast({ type: 'loading', message, duration: 0, ...options });
  }, [addToast]);

  // Función para limpiar todos los toasts
  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{
      toasts,
      addToast,
      removeToast,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      showLoading,
      clearAll,
      position,
      setPosition
    }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} position={position} />
    </ToastContext.Provider>
  );
};

/**
 * Hook para acceder al contexto global de toasts.
 * Debe usarse dentro de ToastProvider.
 */
export const useToastContext = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext debe usarse dentro de ToastProvider');
  }
  return context;
};

/**
 * Hook principal para manejar notificaciones Toast.
 * Usa el contexto global cuando está disponible.
 * @param {Object} options - Opciones de configuración (no utilizado actualmente)
 * @returns {Object} Objeto con funciones para mostrar toasts
 */
export const useToast = (options = {}) => {
  // Usar el contexto global siempre
  const context = useToastContext();
  return context;
};

export default useToast;
